import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Ban, Filter, Mail, Plus, RefreshCw, RotateCcw, Route, Save, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react';
import { emailApi, type FilterRule, type FilterRuleField, type FilterRuleMatch, type InboundEmailLogRow } from '../../api/email';
import { categoriesApi } from '../../api/categories';
import EmptyState from '../EmptyState';
import { useClassifications } from '../../hooks/useClassifications';

// Per-company (Company Settings -> Classification) — an integration's default
// priority has to be one of the values this company actually has.

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  created: { label: 'Ticket created', cls: 'text-ok' },
  replied: { label: 'Reply added', cls: 'text-ok' },
  collapsed: { label: 'Repeat — merged', cls: 'text-ok' },
  crm_created: { label: 'CRM conversation', cls: 'text-ok' },
  crm_replied: { label: 'CRM reply added', cls: 'text-ok' },
  processing: { label: 'Processing', cls: 'text-muted' },
  duplicate: { label: 'Duplicate', cls: 'text-muted' },
  rejected_no_integration: { label: 'No integration', cls: 'text-warn' },
  rejected_loop: { label: 'Auto-reply/loop', cls: 'text-warn' },
  rejected_spam: { label: 'Filtered (spam)', cls: 'text-warn' },
  rejected_filtered: { label: 'Filtered (rule)', cls: 'text-warn' },
  rejected_unknown_recipient: { label: 'Unknown address', cls: 'text-warn' },
  error: { label: 'Error', cls: 'text-err' },
};

const FIELD_LABELS: Record<FilterRuleField, string> = {
  from: 'Sender address',
  from_domain: 'Sender domain',
  subject: 'Subject',
  body: 'Body',
};

const MATCH_LABELS: Record<FilterRuleMatch, string> = {
  contains: 'contains',
  equals: 'equals',
  regex: 'matches regex',
};

function fmtTime(d: string) {
  return new Date(d).toLocaleString();
}

/** Hover text explaining a score: every signal that moved it, largest first.
 *  Negative points are trust signals, so they read as "-2 dmarc_pass". */
function signalTooltip(row: InboundEmailLogRow): string | undefined {
  if (!row.spamSignals || row.spamSignals.length === 0) return undefined;
  return row.spamSignals
    .slice()
    .sort((a, b) => Math.abs(b.points) - Math.abs(a.points))
    .map(s => `${s.points > 0 ? '+' : ''}${s.points} ${s.name}${s.detail ? ` — ${s.detail}` : ''}`)
    .join('\n');
}

function makeRuleId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Editable form of a NotifyRoutingRule — keywords/emails as comma text. */
interface NotifyRuleDraft {
  id: string;
  keywords: string;
  emails: string;
  enabled: boolean;
}

export default function EmailTab() {
  const qc = useQueryClient();

  // Keep a configured default that has since been retired visible, so the
  // form does not silently reassign the integration to something else.
  const { optionsFor } = useClassifications();

  const { data: integration, isLoading } = useQuery({
    queryKey: ['email-integration'],
    queryFn: () => emailApi.getIntegration(),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  const [logPage, setLogPage] = useState(1);
  // '' = everything; 'rejected_spam' = the quarantine review view.
  const [logStatusFilter, setLogStatusFilter] = useState('');
  const { data: logData, isFetching: logsFetching, refetch: refetchLogs } = useQuery({
    queryKey: ['email-logs', logPage, logStatusFilter],
    queryFn: () => emailApi.listLogs(logPage, logStatusFilter || undefined),
  });

  // Form state, hydrated from the loaded integration.
  const [isActive, setIsActive] = useState(true);
  const [inboundEmail, setInboundEmail] = useState('');
  const [defaultCategoryId, setDefaultCategoryId] = useState('');
  const [defaultPriority, setDefaultPriority] = useState('normal');
  const priorityOptions = optionsFor('priority', defaultPriority);
  const [autoReply, setAutoReply] = useState(true);
  const [autoReplyTemplate, setAutoReplyTemplate] = useState('');
  const [aliases, setAliases] = useState('');
  const [allowedDomains, setAllowedDomains] = useState('');
  const [spamThreshold, setSpamThreshold] = useState('');
  const [spamAllowlist, setSpamAllowlist] = useState('');
  const [collapseEnabled, setCollapseEnabled] = useState(true);
  const [collapseWindow, setCollapseWindow] = useState('24');
  const [collapseMachineOnly, setCollapseMachineOnly] = useState(true);
  const [filterRules, setFilterRules] = useState<FilterRule[]>([]);
  const [unmatchedNotifyEmails, setUnmatchedNotifyEmails] = useState('');
  const [notifyRules, setNotifyRules] = useState<NotifyRuleDraft[]>([]);

  useEffect(() => {
    if (!integration) return;
    setIsActive(integration.isActive);
    setInboundEmail(integration.inboundEmail || '');
    setDefaultCategoryId(integration.defaultCategoryId || '');
    setDefaultPriority(integration.defaultPriority || 'normal');
    setAutoReply(integration.settings?.autoReply !== false);
    setAutoReplyTemplate(integration.settings?.autoReplyTemplate || '');
    setAliases((integration.settings?.aliases || []).join(', '));
    setAllowedDomains((integration.settings?.allowedDomains || []).join(', '));
    setSpamThreshold(
      typeof integration.settings?.spamScoreThreshold === 'number'
        ? String(integration.settings.spamScoreThreshold)
        : '',
    );
    setSpamAllowlist((integration.settings?.spamAllowlist || []).join(', '));
    const collapse = integration.settings?.collapseRepeats;
    setCollapseEnabled(collapse?.enabled !== false);
    setCollapseWindow(String(collapse?.windowHours ?? 24));
    setCollapseMachineOnly(collapse?.machineOnly !== false);
    setFilterRules(integration.settings?.filterRules || []);
    setUnmatchedNotifyEmails((integration.settings?.unmatchedNotifyEmails || []).join(', '));
    setNotifyRules((integration.settings?.notifyRoutingRules || []).map(r => ({
      id: r.id,
      keywords: (r.keywords || []).join(', '),
      emails: (r.emails || []).join(', '),
      enabled: r.enabled !== false,
    })));
    // Seed from the loaded integration once. Keying on the object meant every
    // background refetch overwrote whatever was half-edited on screen.
  }, [integration?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const splitList = (s: string) => s.split(',').map(v => v.trim()).filter(Boolean);

  const updateRule = (id: string, patch: Partial<FilterRule>) =>
    setFilterRules(rules => rules.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const removeRule = (id: string) => setFilterRules(rules => rules.filter(r => r.id !== id));
  const addRule = (partial?: Partial<FilterRule>) =>
    setFilterRules(rules => [
      ...rules,
      { id: makeRuleId(), field: 'subject', match: 'contains', value: '', enabled: true, ...partial },
    ]);

  const updateNotifyRule = (id: string, patch: Partial<NotifyRuleDraft>) =>
    setNotifyRules(rules => rules.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const removeNotifyRule = (id: string) => setNotifyRules(rules => rules.filter(r => r.id !== id));
  const addNotifyRule = () =>
    setNotifyRules(rules => [...rules, { id: makeRuleId(), keywords: '', emails: '', enabled: true }]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const parsedThreshold = parseFloat(spamThreshold);
      return emailApi.saveIntegration({
        inboundEmail: inboundEmail.trim(),
        defaultCategoryId: defaultCategoryId || null,
        defaultPriority,
        isActive,
        settings: {
          autoReply,
          autoReplyTemplate,
          aliases: splitList(aliases),
          allowedDomains: splitList(allowedDomains),
          ...(spamThreshold.trim() !== '' && !Number.isNaN(parsedThreshold) && { spamScoreThreshold: parsedThreshold }),
          spamAllowlist: splitList(spamAllowlist),
          collapseRepeats: {
            enabled: collapseEnabled,
            windowHours: Number(collapseWindow) || 24,
            machineOnly: collapseMachineOnly,
          },
          // Drop empty-value rules so a half-filled draft never persists.
          filterRules: filterRules.filter(r => r.value.trim()),
          unmatchedNotifyEmails: splitList(unmatchedNotifyEmails),
          notifyRoutingRules: notifyRules
            .map(r => ({ id: r.id, keywords: splitList(r.keywords), emails: splitList(r.emails), enabled: r.enabled }))
            .filter(r => r.keywords.length > 0 && r.emails.length > 0),
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-integration'] });
      toast.success('Email settings saved');
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'Save failed'),
  });

  const reprocessMutation = useMutation({
    mutationFn: ({ id, notSpam }: { id: string; notSpam?: boolean }) => emailApi.reprocessLog(id, notSpam),
    onSuccess: (result) => {
      if (result.ok) {
        const verb = result.status === 'replied' ? 'reply added to'
          : result.status === 'collapsed' ? 'merged into'
          : 'created';
        toast.success(result.ticketNumber ? `Reprocessed — ${verb} ${result.ticketNumber}` : 'Reprocessed');
      } else {
        toast.error(`Still not processed: ${result.error || result.status}`);
      }
      qc.invalidateQueries({ queryKey: ['email-logs'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message || e.message || 'Reprocess failed'),
  });

  // "Suppress emails like this" — seed a rule from a log row (by sender, the
  // most precise handle for a recurring notifier) and scroll the admin up to
  // the rules editor to review/tweak before saving.
  const suppressFromLog = (row: InboundEmailLogRow) => {
    if (row.fromEmail) {
      addRule({ field: 'from', match: 'equals', value: row.fromEmail });
    } else if (row.subject) {
      addRule({ field: 'subject', match: 'contains', value: row.subject });
    } else {
      return;
    }
    document.getElementById('filter-rules-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    toast('Draft rule added — review it and click Save to apply', { icon: '🛡️' });
  };

  const logs: InboundEmailLogRow[] = logData?.logs ?? [];
  const totalPages = logData ? Math.max(1, Math.ceil(logData.total / logData.limit)) : 1;

  if (isLoading) {
    return <div className="card p-6 text-center text-muted">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="section-title">Email to Ticket</h2>
        <p className="text-sm text-muted mt-1">
          Emails sent to your inbound address become tickets automatically; replies that keep the
          ticket number in the subject are appended to the existing ticket. Have your mail admin
          forward your public support address (e.g. support@yourcompany.com) to the inbound address below.
        </p>
      </div>

      {/* Settings */}
      <div className="card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="label">Enable inbound email</span>
            <p className="text-xs text-muted">When off, mail to this address is rejected at the server.</p>
          </div>
          <input type="checkbox" className="w-4 h-4" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Inbound address</label>
            <input
              className="input w-full"
              placeholder="acme@in.yourplatform.com"
              value={inboundEmail}
              onChange={e => setInboundEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Aliases (comma-separated)</label>
            <input
              className="input w-full"
              placeholder="helpdesk@in.yourplatform.com, support@acme.com"
              value={aliases}
              onChange={e => setAliases(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Default category</label>
            <select className="select w-full" value={defaultCategoryId} onChange={e => setDefaultCategoryId(e.target.value)}>
              <option value="">None (AI may pick one)</option>
              {categories.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Default priority</label>
            <select className="select w-full" value={defaultPriority} onChange={e => setDefaultPriority(e.target.value)}>
              {priorityOptions.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label">Only accept mail from these domains (comma-separated, optional)</label>
            <input
              className="input w-full"
              placeholder="acme.com, partner.org — leave empty to accept from anyone"
              value={allowedDomains}
              onChange={e => setAllowedDomains(e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="label">Spam score threshold</label>
            <input
              type="number"
              step="0.1"
              className="input w-full md:w-40"
              placeholder="7"
              value={spamThreshold}
              onChange={e => setSpamThreshold(e.target.value)}
            />
            <p className="text-xs text-muted mt-1">
              Mail scoring above this is quarantined instead of becoming a ticket — it still
              appears in the log below, where you can review it and restore anything caught by
              mistake. Scores come from sender authentication (SPF/DKIM/DMARC), the sending
              server's reputation, and message shape. Lower = more aggressive; leave empty for
              the default (7). Senders who are members of your company, or who already have
              tickets with you, are scored far below any threshold.
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="label">Never filter mail from (comma-separated, optional)</label>
            <input
              className="input w-full"
              placeholder="billing@acme.com, partner.org"
              value={spamAllowlist}
              onChange={e => setSpamAllowlist(e.target.value)}
            />
            <p className="text-xs text-muted mt-1">
              Addresses or bare domains that skip spam scoring entirely. Use this for a sender
              that keeps getting quarantined. Suppression rules still apply.
            </p>
          </div>

          <div className="md:col-span-2 border-t border-line pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={collapseEnabled}
                onChange={e => setCollapseEnabled(e.target.checked)}
              />
              <span className="label mb-0">Merge repeated alerts into one ticket</span>
            </label>
            <p className="text-xs text-muted mt-1">
              When the same sender emails the same subject again and a ticket for it is still
              open, add it to that ticket instead of opening another. Built for monitoring
              systems that re-send an alert every cycle. Volatile parts of the subject (error
              ids, timestamps) are ignored when comparing, and merged messages don't re-notify.
              A repeat that arrives after the ticket is resolved always opens a new one.
            </p>
            {collapseEnabled && (
              <div className="flex flex-wrap items-center gap-4 mt-3 pl-6">
                <div>
                  <label className="label">Look back (hours)</label>
                  <input
                    type="number"
                    min={1}
                    max={720}
                    className="input w-28"
                    value={collapseWindow}
                    onChange={e => setCollapseWindow(e.target.value)}
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-5">
                  <input
                    type="checkbox"
                    checked={collapseMachineOnly}
                    onChange={e => setCollapseMachineOnly(e.target.checked)}
                  />
                  <span className="text-sm text-fg">Only automated senders</span>
                </label>
                <p className="text-xs text-muted basis-full">
                  Leaving "only automated senders" on restricts merging to mail that looks
                  machine-generated (alert/monitor/no-reply addresses, or automation headers).
                  Turn it off to merge repeated subjects from people too.
                </p>
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="label">Notify when no product line matches (comma-separated)</label>
            <input
              className="input w-full"
              placeholder="triage@yourcompany.com"
              value={unmatchedNotifyEmails}
              onChange={e => setUnmatchedNotifyEmails(e.target.value)}
            />
            <p className="text-xs text-muted mt-1">
              These addresses get a content-free alert when an email ticket can't be routed to a
              product line (or the detected line was dropped because it requires fields email can't
              provide), so someone can triage it. Product lines are detected via each line's routing
              keywords, with AI as a fallback.
            </p>
          </div>
        </div>

        <div className="border-t border-line pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="label">Auto-reply confirmation</span>
              <p className="text-xs text-muted">
                Send the sender a "ticket received" confirmation with a secure link to view their ticket.
              </p>
            </div>
            <input type="checkbox" className="w-4 h-4" checked={autoReply} onChange={e => setAutoReply(e.target.checked)} />
          </div>
          {autoReply && (
            <div>
              <label className="label">Custom auto-reply (HTML, optional)</label>
              <textarea
                className="input w-full h-28 font-mono text-xs"
                placeholder="Leave empty to send the standard confirmation with a secure ticket link."
                value={autoReplyTemplate}
                onChange={e => setAutoReplyTemplate(e.target.value)}
              />
              <p className="text-xs text-warn mt-1">
                Do not include ticket content or patient/customer details in the template — confirmation
                emails are intentionally content-free.
              </p>
            </div>
          )}
        </div>

        {/* Suppression rules */}
        <div id="filter-rules-card" className="border-t border-line pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="label flex items-center gap-1.5">
                <Filter className="w-4 h-4" /> Filter rules
              </span>
              <p className="text-xs text-muted">
                Drop matching mail before it becomes a ticket — e.g. recurring "failed login"
                notifications. Rules are checked in order; the first match wins and the email is
                logged as <span className="badge text-warn">Filtered (rule)</span> with no ticket created.
              </p>
            </div>
            <button type="button" className="btn btn-secondary text-sm shrink-0" onClick={() => addRule()}>
              <Plus className="w-4 h-4" /> Add rule
            </button>
          </div>

          {filterRules.length === 0 ? (
            <p className="text-xs text-muted italic">
              No filter rules. Add one above, or use "Suppress like this" on a row in the log below.
            </p>
          ) : (
            <div className="space-y-2">
              {filterRules.map(rule => (
                <div key={rule.id} className="flex flex-wrap items-center gap-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 shrink-0"
                    checked={rule.enabled}
                    title={rule.enabled ? 'Enabled' : 'Disabled'}
                    onChange={e => updateRule(rule.id, { enabled: e.target.checked })}
                  />
                  <select
                    className="select w-40"
                    value={rule.field}
                    onChange={e => updateRule(rule.id, { field: e.target.value as FilterRuleField })}
                  >
                    {(Object.keys(FIELD_LABELS) as FilterRuleField[]).map(f => (
                      <option key={f} value={f}>{FIELD_LABELS[f]}</option>
                    ))}
                  </select>
                  <select
                    className="select w-40"
                    value={rule.match}
                    onChange={e => updateRule(rule.id, { match: e.target.value as FilterRuleMatch })}
                  >
                    {(Object.keys(MATCH_LABELS) as FilterRuleMatch[]).map(m => (
                      <option key={m} value={m}>{MATCH_LABELS[m]}</option>
                    ))}
                  </select>
                  <input
                    className="input flex-1 min-w-[12rem]"
                    placeholder={rule.match === 'regex' ? '^Failed login attempt' : 'failed login'}
                    value={rule.value}
                    onChange={e => updateRule(rule.id, { value: e.target.value })}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost text-sm text-err shrink-0"
                    title="Delete rule"
                    onClick={() => removeRule(rule.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <p className="text-xs text-muted">
                Matching is case-insensitive. A rule blocks replies too, so target machine noise —
                not a keyword a customer might legitimately use.
              </p>
            </div>
          )}
        </div>

        {/* New-ticket alert routing */}
        <div className="border-t border-line pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="label flex items-center gap-1.5">
                <Route className="w-4 h-4" /> New-ticket alert routing
              </span>
              <p className="text-xs text-muted">
                When an incoming email's subject contains any of a rule's keywords, the new-ticket
                alert goes to that rule's addresses <em>instead of</em> the default notification
                list (General settings). Rules are checked in order; the first match wins. Emails
                matching no rule use the default list as usual.
              </p>
            </div>
            <button type="button" className="btn btn-secondary text-sm shrink-0" onClick={addNotifyRule}>
              <Plus className="w-4 h-4" /> Add rule
            </button>
          </div>

          {notifyRules.length === 0 ? (
            <p className="text-xs text-muted italic">
              No routing rules — every email ticket alerts the default notification list.
            </p>
          ) : (
            <div className="space-y-2">
              {notifyRules.map(rule => (
                <div key={rule.id} className="flex flex-wrap items-center gap-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 shrink-0"
                    checked={rule.enabled}
                    title={rule.enabled ? 'Enabled' : 'Disabled'}
                    onChange={e => updateNotifyRule(rule.id, { enabled: e.target.checked })}
                  />
                  <input
                    className="input flex-1 min-w-[10rem]"
                    placeholder="Keywords: roof repair, gutter"
                    title="Comma-separated keywords matched against the email subject"
                    value={rule.keywords}
                    onChange={e => updateNotifyRule(rule.id, { keywords: e.target.value })}
                  />
                  <span className="text-xs text-muted shrink-0">notify</span>
                  <input
                    className="input flex-1 min-w-[12rem]"
                    placeholder="roofing@yourcompany.com"
                    title="Comma-separated recipient addresses"
                    value={rule.emails}
                    onChange={e => updateNotifyRule(rule.id, { emails: e.target.value })}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost text-sm text-err shrink-0"
                    title="Delete rule"
                    onClick={() => removeNotifyRule(rule.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <p className="text-xs text-muted">
                Keyword matching is case-insensitive against the email subject. A rule needs at
                least one keyword and one address to be saved.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            className="btn btn-primary"
            disabled={!inboundEmail.trim() || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </div>

      {/* Inbound mail log */}
      <div className="card overflow-hidden p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div>
            <h3 className="font-medium text-fg">
              {logStatusFilter === 'rejected_spam' ? 'Quarantined mail' : 'Recent inbound mail'}
            </h3>
            <p className="text-xs text-muted">
              {logStatusFilter === 'rejected_spam'
                ? 'Filtered as spam and never turned into a ticket. Anything here that is real can be restored with "Not spam".'
                : 'Why each email did or didn\'t become a ticket. Mail rejected before matching your address only appears in server logs.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`btn text-sm ${logStatusFilter === 'rejected_spam' ? 'btn-secondary' : 'btn-ghost'}`}
              title="Show only mail that was quarantined as spam"
              onClick={() => {
                setLogPage(1);
                setLogStatusFilter(f => (f === 'rejected_spam' ? '' : 'rejected_spam'));
              }}
            >
              <ShieldAlert className="w-4 h-4" />
              {logStatusFilter === 'rejected_spam' ? 'Show all mail' : 'Quarantine'}
            </button>
            <button className="btn btn-ghost text-sm" onClick={() => refetchLogs()} disabled={logsFetching}>
              <RefreshCw className={`w-4 h-4 ${logsFetching ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
        {logs.length === 0 ? (
          <EmptyState icon={<Mail className="w-6 h-6" />}>
            No inbound email yet. Once mail arrives at your inbound address it will show up here.
          </EmptyState>
        ) : (
          <>
            <table className="scpa-table">
              <thead>
                <tr>
                  <th className="scpa-th">Received</th>
                  <th className="scpa-th">From</th>
                  <th className="scpa-th">Subject</th>
                  <th className="scpa-th">Outcome</th>
                  <th className="scpa-th">Score</th>
                  <th className="scpa-th">Ticket</th>
                  <th className="scpa-th"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map(row => {
                  const badge = STATUS_BADGE[row.status] || { label: row.status, cls: 'text-muted' };
                  return (
                    <tr key={row.id} className="scpa-row" style={{ cursor: 'default' }}>
                      <td className="scpa-td text-muted whitespace-nowrap">{fmtTime(row.createdAt)}</td>
                      <td className="scpa-td text-fg">{row.fromEmail}</td>
                      <td className="scpa-td text-muted max-w-[280px] truncate" title={row.subject}>{row.subject || '—'}</td>
                      <td className="scpa-td">
                        <span className={`badge ${badge.cls}`} title={row.error || undefined}>{badge.label}</span>
                      </td>
                      <td className="scpa-td font-mono text-xs" title={signalTooltip(row)}>
                        {typeof row.spamScore === 'number' ? (
                          <span className={row.spamScore >= 7 ? 'text-err' : row.spamScore > 3 ? 'text-warn' : 'text-muted'}>
                            {row.spamScore}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="scpa-td font-mono text-xs text-muted">{row.ticketNumber || (row.conversationId ? <a href={`/crm/inbox/${row.conversationId}`} className="text-primary hover:underline">CRM-{row.conversationNumber}</a> : '—')}</td>
                      <td className="scpa-td text-right whitespace-nowrap">
                        <button
                          className="btn btn-ghost text-sm"
                          title="Add a filter rule to suppress future emails like this"
                          onClick={() => suppressFromLog(row)}
                        >
                          <Ban className="w-4 h-4" /> Suppress like this
                        </button>
                        {row.canReprocess && row.status === 'rejected_spam' && (
                          <button
                            className="btn btn-ghost text-sm"
                            title="This was a real message — turn it into a ticket and skip spam scoring"
                            disabled={reprocessMutation.isPending}
                            onClick={() => reprocessMutation.mutate({ id: row.id, notSpam: true })}
                          >
                            <ShieldCheck className={`w-4 h-4 ${reprocessMutation.isPending && reprocessMutation.variables?.id === row.id ? 'animate-spin' : ''}`} />
                            Not spam
                          </button>
                        )}
                        {row.canReprocess && row.status !== 'rejected_spam' && (
                          <button
                            className="btn btn-ghost text-sm"
                            title="Run this email through the pipeline again"
                            disabled={reprocessMutation.isPending}
                            onClick={() => reprocessMutation.mutate({ id: row.id })}
                          >
                            <RotateCcw className={`w-4 h-4 ${reprocessMutation.isPending && reprocessMutation.variables?.id === row.id ? 'animate-spin' : ''}`} />
                            Reprocess
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-line text-sm text-muted">
                <button className="btn btn-ghost text-sm" disabled={logPage <= 1} onClick={() => setLogPage(p => p - 1)}>
                  Previous
                </button>
                <span>Page {logPage} of {totalPages}</span>
                <button className="btn btn-ghost text-sm" disabled={logPage >= totalPages} onClick={() => setLogPage(p => p + 1)}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
